import { useScrollReveal } from '@/hooks/useScrollReveal';

const fadeIn = (visible: boolean, delay: string) => ({
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(24px)',
  transition: 'all 700ms ease-out',
  transitionDelay: delay,
});

export default function MobileAppSection() {
  const [cardRef, cardVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.15, rootMargin: '0px 0px -60px 0px' });

  return (
    <section id="download-app" className="relative py-20 md:py-28 px-4 md:px-8 lg:px-12 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-background-100" />

      {/* Decorative gradient blob */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-primary-700/15 via-primary-800/5 to-transparent rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left: Phone Mockup */}
          <div className="w-full lg:w-5/12 flex justify-center lg:justify-end">
            <div className="relative w-full max-w-sm">
              {/* Glow behind phone */}
              <div className="absolute inset-0 bg-gradient-to-b from-primary-600/30 via-primary-800/10 to-transparent rounded-[3rem] blur-2xl scale-110" />

              <div className="relative overflow-hidden rounded-[2.5rem] border-2 border-primary-700/40 shadow-2xl aspect-[1/2] bg-gradient-to-b from-background-50 via-background-100 to-background-50">
                {/* Subtle gold ambient glow */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[200px] h-[120px] bg-gradient-radial from-primary-500/15 to-transparent rounded-full blur-2xl" />

                {/* === APP UI === */}
                <div className="relative z-10 flex flex-col h-full">

                  {/* Status Bar */}
                  <div className="flex items-center justify-between px-6 pt-4 pb-2">
                    <span className="text-foreground-300 text-[11px] font-label font-semibold">9:41</span>
                    <div className="flex items-center gap-1">
                      <i className="ri-signal-wifi-line text-foreground-300 text-[11px]" />
                      <i className="ri-battery-2-fill text-foreground-300 text-[11px]" />
                    </div>
                  </div>

                  {/* Greeting */}
                  <div className="px-6 pt-1 pb-4">
                    <p className="text-foreground-400 text-[11px] font-body uppercase tracking-wider">Good Morning</p>
                    <p className="text-foreground-100 font-heading text-lg font-bold">T.M.T</p>
                  </div>

                  {/* Platinum VISA Card */}
                  <div className="mx-4 mb-4" ref={cardRef} style={fadeIn(cardVisible, '200ms')}>
                    {/* Card surface */}
                    <div className="relative rounded-2xl overflow-hidden border border-primary-500/30" style={{
                      background: 'linear-gradient(135deg, oklch(var(--primary-400)) 0%, oklch(var(--primary-500)) 20%, oklch(var(--primary-300)) 40%, oklch(var(--primary-600)) 60%, oklch(var(--primary-400)) 80%, oklch(var(--primary-500)) 100%)',
                    }}>
                      {/* Subtle brushed metal texture overlay */}
                      <div className="absolute inset-0 opacity-25" style={{
                        background: 'repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255,255,255,0.04) 2px, rgba(255,255,255,0.04) 4px)',
                      }} />

                      <div className="relative p-5">
                        {/* Top row: label + VISA logo */}
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-primary-200/70 text-[9px] font-label uppercase tracking-[0.2em]">Gold Debit</span>
                          <div className="flex items-center gap-1">
                            <span className="font-label text-[15px] font-bold italic tracking-wider text-primary-100/80" style={{ fontFamily: 'var(--font-heading)' }}>VISA</span>
                          </div>
                        </div>

                        {/* EMV Chip */}
                        <div className="mb-3 flex items-center gap-2">
                          <div className="w-9 h-7 rounded-[4px] border border-primary-500/30" style={{
                            background: 'linear-gradient(135deg, oklch(var(--primary-300)) 0%, oklch(var(--primary-500)) 50%, oklch(var(--primary-400)) 100%)',
                          }}>
                            <div className="flex h-full">
                              <div className="w-1/2 border-r border-primary-600/20" />
                              <div className="w-1/2" />
                            </div>
                          </div>
                          <i className="ri-wifi-line text-primary-400/30 text-xs rotate-90" />
                        </div>

                        {/* Card Number */}
                        <p className="text-primary-100/90 font-label text-[15px] tracking-[0.16em] mb-3" style={{ fontFamily: '"Courier New", monospace', letterSpacing: '0.16em' }}>
                          XXXX XXXX XXXX 4829
                        </p>

                        {/* Bottom row: holder + expiry */}
                        <div className="flex items-end justify-between">
                          <div>
                            <span className="block text-primary-200/50 text-[7px] font-label uppercase tracking-wider mb-0.5">Card Holder</span>
                            <span className="text-primary-100/80 font-label text-[10px] tracking-[0.06em]">T.M.T</span>
                          </div>
                          <div className="text-right">
                            <span className="block text-primary-200/50 text-[7px] font-label uppercase tracking-wider mb-0.5">Valid Thru</span>
                            <span className="text-primary-100/80 font-label text-[10px] tracking-[0.08em]">08/29</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Balance row below card */}
                    <div className="flex items-center justify-between mt-2.5 px-1">
                      <div>
                        <span className="text-foreground-500 text-[9px] font-label uppercase tracking-wider">Available Balance</span>
                        <p className="text-foreground-100 font-heading text-xl font-bold tracking-tight">$48,250.00</p>
                      </div>
                      <span className="text-accent-400 text-[10px] font-body flex items-center gap-1">
                        <i className="ri-arrow-up-line text-[10px]" />
                        +12.4%
                      </span>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex justify-around px-4 mb-5">
                    <div className="flex flex-col items-center gap-1.5 cursor-pointer">
                      <div className="w-10 h-10 rounded-full bg-primary-500/15 flex items-center justify-center border border-primary-600/20">
                        <i className="ri-arrow-up-circle-line text-primary-400 text-base" />
                      </div>
                      <span className="text-foreground-400 text-[10px] font-body">Send</span>
                    </div>
                    <div className="flex flex-col items-center gap-1.5 cursor-pointer">
                      <div className="w-10 h-10 rounded-full bg-primary-500/15 flex items-center justify-center border border-primary-600/20">
                        <i className="ri-arrow-down-circle-line text-primary-400 text-base" />
                      </div>
                      <span className="text-foreground-400 text-[10px] font-body">Receive</span>
                    </div>
                    <div className="flex flex-col items-center gap-1.5 cursor-pointer">
                      <div className="w-10 h-10 rounded-full bg-primary-500/15 flex items-center justify-center border border-primary-600/20">
                        <i className="ri-smartphone-line text-primary-400 text-base" />
                      </div>
                      <span className="text-foreground-400 text-[10px] font-body">Scan</span>
                    </div>
                    <div className="flex flex-col items-center gap-1.5 cursor-pointer">
                      <div className="w-10 h-10 rounded-full bg-primary-500/15 flex items-center justify-center border border-primary-600/20">
                        <i className="ri-swap-line text-primary-400 text-base" />
                      </div>
                      <span className="text-foreground-400 text-[10px] font-body">Swap</span>
                    </div>
                    <div className="flex flex-col items-center gap-1.5 cursor-pointer">
                      <div className="w-10 h-10 rounded-full bg-primary-500/15 flex items-center justify-center border border-primary-600/20">
                        <i className="ri-bank-card-line text-primary-400 text-base" />
                      </div>
                      <span className="text-foreground-400 text-[10px] font-body">Cards</span>
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <div className="flex-1 px-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-foreground-200 text-[11px] font-label font-semibold uppercase tracking-wider">Recent Activity</span>
                      <span className="text-primary-400 text-[10px] font-body">See All</span>
                    </div>

                    <div className="flex flex-col gap-2.5">
                      {/* Transaction 1 */}
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-accent-500/15 flex items-center justify-center flex-shrink-0 border border-accent-600/20">
                          <i className="ri-arrow-down-circle-fill text-accent-400 text-sm" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-foreground-200 text-[12px] font-body font-medium truncate">Salary Deposit</p>
                          <p className="text-foreground-500 text-[10px] font-body">Jul 18 · Completed</p>
                        </div>
                        <span className="text-accent-400 text-[12px] font-label font-semibold whitespace-nowrap">+$12,500</span>
                      </div>

                      {/* Transaction 2 */}
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-background-200/70 flex items-center justify-center flex-shrink-0 border border-background-300/40">
                          <i className="ri-apple-fill text-foreground-300 text-sm" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-foreground-200 text-[12px] font-body font-medium truncate">Apple Store</p>
                          <p className="text-foreground-500 text-[10px] font-body">Jul 17 · Completed</p>
                        </div>
                        <span className="text-foreground-300 text-[12px] font-label font-semibold whitespace-nowrap">-$349.99</span>
                      </div>

                      {/* Transaction 3 */}
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-primary-500/15 flex items-center justify-center flex-shrink-0 border border-primary-600/20">
                          <i className="ri-arrow-up-circle-fill text-primary-400 text-sm" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-foreground-200 text-[12px] font-body font-medium truncate">Transfer to David</p>
                          <p className="text-foreground-500 text-[10px] font-body">Jul 17 · Pending</p>
                        </div>
                        <span className="text-primary-400 text-[12px] font-label font-semibold whitespace-nowrap">-$2,850</span>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Navigation */}
                  <div className="flex items-center justify-around px-2 py-3 border-t border-primary-800/20 mt-auto">
                    <div className="flex flex-col items-center gap-0.5 cursor-pointer">
                      <i className="ri-home-4-fill text-primary-400 text-base" />
                      <span className="text-primary-400 text-[9px] font-body">Home</span>
                    </div>
                    <div className="flex flex-col items-center gap-0.5 cursor-pointer">
                      <i className="ri-briefcase-line text-foreground-500 text-base" />
                      <span className="text-foreground-500 text-[9px] font-body">Services</span>
                    </div>
                    <div className="flex flex-col items-center gap-0.5 cursor-pointer">
                      <i className="ri-bank-card-2-line text-foreground-500 text-base" />
                      <span className="text-foreground-500 text-[9px] font-body">Cards</span>
                    </div>
                    <div className="flex flex-col items-center gap-0.5 cursor-pointer">
                      <i className="ri-user-3-line text-foreground-500 text-base" />
                      <span className="text-foreground-500 text-[9px] font-body">Profile</span>
                    </div>
                  </div>

                </div>
              </div>

              {/* Floating badge */}
              <div className="absolute -bottom-3 -right-3 md:-bottom-5 md:-right-5 bg-background-50 border border-primary-700/30 rounded-xl px-4 py-3 shadow-xl animate-float-slow">
                <div className="flex items-center gap-2">
                  <div className="flex items-center">
                    <i className="ri-star-fill text-primary-400 text-sm" />
                    <i className="ri-star-fill text-primary-400 text-sm" />
                    <i className="ri-star-fill text-primary-400 text-sm" />
                    <i className="ri-star-fill text-primary-400 text-sm" />
                    <i className="ri-star-half-fill text-primary-400 text-sm" />
                  </div>
                  <span className="text-foreground-100 font-label text-sm font-semibold whitespace-nowrap">4.8 / 5</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Content & Download Buttons */}
          <div className="w-full lg:w-7/12">
            <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-3">
              / Mobile App
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-foreground-50 leading-tight mb-4">
              Your Wallet
              <br />
              <span className="text-primary-400">In Your Pocket</span>
            </h2>
            <p className="text-foreground-300 text-base leading-relaxed mb-8 max-w-lg font-body">
              Download the TMT mobile app and take full control of your finances — anytime, anywhere. Send money, pay bills, manage digital cards, and track every transaction in real time, all from one beautifully designed app.
            </p>

            {/* Feature bullets */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/15 flex-shrink-0">
                  <i className="ri-flashlight-line text-primary-400" />
                </div>
                <span className="text-foreground-300 text-sm font-body">Instant Transfers</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/15 flex-shrink-0">
                  <i className="ri-fingerprint-line text-primary-400" />
                </div>
                <span className="text-foreground-300 text-sm font-body">Biometric Security</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/15 flex-shrink-0">
                  <i className="ri-smartphone-line text-primary-400" />
                </div>
                <span className="text-foreground-300 text-sm font-body">Scan to Pay</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/15 flex-shrink-0">
                  <i className="ri-global-line text-primary-400" />
                </div>
                <span className="text-foreground-300 text-sm font-body">14+ Corridors</span>
              </div>
            </div>

            {/* Download Buttons */}
            <div className="flex flex-col sm:flex-row items-start gap-4">
              {/* App Store */}
              <a
                href="https://apps.apple.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 px-6 py-3.5 bg-foreground-50 text-background-50 rounded-xl hover:bg-foreground-100 transition-all duration-300 hover:-translate-y-0.5"
              >
                <i className="ri-apple-fill text-2xl" />
                <div className="text-left leading-tight">
                  <p className="text-[10px] font-body opacity-70">Download on the</p>
                  <p className="font-label text-base font-semibold whitespace-nowrap">App Store</p>
                </div>
              </a>

              {/* Google Play */}
              <a
                href="https://play.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 px-6 py-3.5 bg-foreground-50 text-background-50 rounded-xl hover:bg-foreground-100 transition-all duration-300 hover:-translate-y-0.5"
              >
                <i className="ri-google-play-fill text-2xl" />
                <div className="text-left leading-tight">
                  <p className="text-[10px] font-body opacity-70">Get it on</p>
                  <p className="font-label text-base font-semibold whitespace-nowrap">Google Play</p>
                </div>
              </a>
            </div>

            {/* QR Code hint */}
            <p className="mt-5 text-foreground-500 text-xs font-body flex items-center gap-2">
              <i className="ri-qr-scan-line" />
              Or scan the QR code on the app to download instantly
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}